import { createIsomorphicFn } from '@tanstack/react-start';
import { queryOptions } from '@tanstack/react-query';
import { authClient } from '@repo/shared/client';
import { toAppSession, type AppSession } from '../session-shared';

/** Hierarchical query key for the caller's session. */
export const sessionQueryKey = ['auth', 'session'] as const;

/**
 * Isomorphic session fetcher. On the server it resolves in-process from the
 * request cookies; on the client it asks BetterAuth's client. Same rules as
 * `createContractFetcher`: single chained expression, and the server module is a
 * **dynamic `import()`** so it's stripped from the browser build *and* never
 * evaluated under vitest/Node (where `cloudflare:workers` can't resolve).
 */
const fetchSession = createIsomorphicFn()
  .server(async (): Promise<AppSession | null> => {
    const { resolveSessionOnServer } = await import('../session.server');
    return resolveSessionOnServer();
  })
  .client(async (): Promise<AppSession | null> => {
    const { data } = await authClient.getSession();
    return data ? toAppSession(data.user) : null;
  });

/**
 * Query options for the caller's session — the single definition the route
 * `beforeLoad` auth guards (`ensureQueryData`) and any reactive consumer
 * (`useQuery`) share. Inherits the app QueryClient's 5-minute `staleTime`, so a
 * client navigation reuses the resolved session instead of refetching each hop.
 *
 * Cache sync points: sign-in/up clears this key (so the freshly-authenticated
 * session is fetched), and sign-out clears the whole client. A future Profile
 * edit that changes the user should `invalidateQueries({ queryKey:
 * sessionQueryKey })`.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand; the AppSession generic keeps callers typed.
export function sessionQueryOptions() {
  return queryOptions<AppSession | null>({
    queryKey: sessionQueryKey,
    queryFn: () => fetchSession(),
  });
}
